import { ChartTypeRegistry, Point, BubbleDataPoint, TooltipModel, Chart } from 'chart.js';
import { Tooltip } from './types';
import DOMPurify from 'dompurify';

const sanitizer = DOMPurify.sanitize;

const getOrCreateTooltip = (chart: Chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    //Change color names?
    tooltipEl = document.createElement('div');
    tooltipEl.style.background = 'white';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.borderStyle = 'solid';
    tooltipEl.style.borderWidth = '1px';
    tooltipEl.style.borderColor = '#808080';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .1s ease';
    //Is it ok to use zIndex?
    tooltipEl.style.zIndex = '99';

    const table = document.createElement('table');

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

export type ContextType = {
  chart: Chart<keyof ChartTypeRegistry, (number | [number, number] | Point | BubbleDataPoint)[], unknown>;
  tooltip: TooltipModel<keyof ChartTypeRegistry>;
};

export const externalTooltipHandler = (context: ContextType, tooltipHelper: Tooltip) => {
  const { chart, tooltip } = context;
  const {
    data: { datasets },
  } = chart;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  const titleLines = tooltip.title || [];

  const tableHead = document.createElement('thead');

  const textSize = '12px';

  titleLines.forEach(title => {
    const tr = document.createElement('tr');
    tr.style.borderWidth = '0px 0px 1px 0px';
    tr.style.borderStyle = 'solid';
    tr.style.borderColor = 'grey';

    const th = document.createElement('th');
    th.style.textAlign = 'left';
    th.style.color = 'grey';
    th.style.fontSize = textSize;
    th.style.paddingBottom = '4px';
    th.style.fontWeight = '900';

    const text = document.createTextNode(title);

    th.appendChild(text);
    tr.appendChild(th);
    tableHead.appendChild(tr);
  });

  const tooltipIndex = tooltip.dataPoints[0].dataIndex;

  const tableBody = document.createElement('tbody');

  //Reverse mutates the actual dataset, so create array to stop this behaviour
  [...datasets].reverse().forEach(set => {
    const { label, data, borderColor } = set;
    const value = Array.isArray(data[tooltipIndex]) ? data[tooltipIndex][0] : data[tooltipIndex];
    const color = borderColor[tooltipIndex];

    //Circle colour
    const span = document.createElement('span');
    span.style.background = color;
    span.style.borderRadius = '10px';
    span.style.marginTop = '4px';
    span.style.marginRight = '10px';
    span.style.height = '10px';
    span.style.width = '10px';
    span.style.display = 'inline-block';

    const tr = document.createElement('tr');
    tr.style.backgroundColor = 'inherit';

    const td = document.createElement('td');
    td.style.paddingTop = '4px';
    td.style.color = 'grey';
    td.style.fontWeight = '500';

    const container = document.createElement('div');
    const textContainer = document.createElement('div');

    container.style.display = 'flex';
    container.style.fontSize = textSize;
    textContainer.style.display = 'flex';
    textContainer.style.justifyContent = 'space-between';
    textContainer.style.width = '100%';

    const valueDiv = document.createElement('div');
    valueDiv.textContent = tooltipHelper?.valueFormatter(value as number);
    valueDiv.style.fontWeight = 'bold';

    const labelDiv = document.createElement('div');
    labelDiv.textContent = label;
    labelDiv.style.paddingRight = '16px';

    textContainer.appendChild(labelDiv);
    textContainer.appendChild(valueDiv);

    container.appendChild(span);
    container.appendChild(textContainer);

    td.appendChild(container);
    tr.appendChild(td);
    tableBody.appendChild(tr);
  });

  const tableRoot = tooltipEl.querySelector('table');

  while (tableRoot.firstChild) {
    tableRoot.firstChild.remove();
  }

  tableRoot.appendChild(tableHead);
  tableRoot.appendChild(tableBody);

  //Tooltip stays in canvas
  const offset = 100;
  const positionX = tooltip.caretX;
  const positionY = offset;

  if (![0, 1].includes(tooltipIndex) && tooltip.caretX > chart.width - tooltip.width - offset) {
    tooltipEl.style.left = `${positionX - offset}px`;
  } else {
    tooltipEl.style.left = `${positionX + offset}px`;
  }

  tooltipEl.style.opacity = '1';
  tooltipEl.style.top = `${positionY}px`;
  tooltipEl.style.padding = `${tooltip.options.padding}px ${tooltip.options.padding}px`;
};

export const renderTooltip = (context: ContextType) => {
  let fadeOutTimeout: number | null = null;

  const tooltipModel = context.tooltip;
  let tooltipEl = document.getElementById('chart-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chart-tooltip';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.background = 'rgba(0, 0, 0, 0.8)';
    tooltipEl.style.color = 'white';
    tooltipEl.style.borderRadius = '6px';
    tooltipEl.style.padding = '6px';
    tooltipEl.style.fontSize = '14px';
    tooltipEl.style.pointerEvents = 'none';
    // Smooth transitions for opacity and position
    tooltipEl.style.transition = 'opacity 0.3s ease-in-out, top 0.3s ease-in-out, left 0.3s ease-in-out';
    document.body.appendChild(tooltipEl);
  }
  // (Re)append the arrow element after setting innerHTML

  let arrowEl = tooltipEl.querySelector('.tooltip-arrow');
  if (!arrowEl) {
    arrowEl = document.createElement('div');
    arrowEl.className = 'tooltip-arrow';
  }
  // If the tooltip should be hidden, fade it out
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  // Build the tooltip content (excluding the arrow)
  const title = tooltipModel.title || [];
  let innerHtml = `<div style="font-weight: bold; margin-bottom: 4px;">${title[0]}</div>`;
  tooltipModel.body.forEach((body, index) => {
    const color = tooltipModel.labelColors[index].backgroundColor;
    innerHtml += `
      <div style="display: flex; align-items: center;">
        <span style="background-color: ${color}; width: 12px; height: 12px; display: inline-block; margin-right: 5px;"></span>
        <span>${body.lines[0]}</span>
      </div>
    `;
  });
  // If there is a pending fade-out, cancel it
  if (fadeOutTimeout) {
    clearTimeout(fadeOutTimeout);
  }

  // Update content
  tooltipEl.innerHTML = sanitizer(innerHtml);
  tooltipEl.appendChild(arrowEl);

  updateTooltipPosition(tooltipEl, arrowEl as HTMLElement, context);

  // Ensure tooltip is visible immediately
  tooltipEl.style.opacity = '1';
};

function updateTooltipPosition(tooltipEl: HTMLElement, arrowEl: HTMLElement, context: ContextType) {
  const tooltipModel = context.tooltip;
  const canvasRect = context.chart.canvas.getBoundingClientRect();

  // Calculate the initial left and top positions based on caret coordinates.
  let left = canvasRect.left + window.scrollX + tooltipModel.caretX;
  const top = canvasRect.top + window.scrollY + tooltipModel.caretY - 10;

  // Prevent the tooltip from overflowing off the right edge of the window.
  const tooltipWidth = tooltipEl.offsetWidth;
  const extraTooltipSpace = 20;
  const availableSpaceRight = window.innerWidth - left;
  if (availableSpaceRight < tooltipWidth + extraTooltipSpace) {
    left = left - tooltipWidth;
    arrowEl.removeAttribute('style');
    arrowEl.style.backgroundColor = 'lightblue';
    arrowEl.style.position = 'absolute';
    arrowEl.style.width = '0';
    arrowEl.style.height = '0';
    arrowEl.style.backgroundColor = 'transparent';
    arrowEl.style.borderBottom = '5px solid transparent';
    arrowEl.style.borderTop = '5px solid transparent';
    arrowEl.style.borderLeft = '5px solid rgba(0, 0, 0, 0.8)';
    arrowEl.style.borderRight = 'none';
    arrowEl.style.left = '100%';
  } else {
    arrowEl.removeAttribute('style');
    arrowEl.style.backgroundColor = 'lightblue';
    arrowEl.style.position = 'absolute';
    arrowEl.style.width = '0';
    arrowEl.style.height = '0';
    arrowEl.style.backgroundColor = 'transparent';
    arrowEl.style.borderTop = '5px solid transparent';
    arrowEl.style.borderBottom = '5px solid transparent';
    arrowEl.style.borderRight = '5px solid rgba(0, 0, 0, 0.8)';
    arrowEl.style.borderLeft = 'none';
    arrowEl.style.left = '-5px';
  }

  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
}
